import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Plus, Filter, Download, Edit, Trash2 } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import ProfitDistributionPanel from '../../../components/trading/ProfitDistributionPanel';

type Allocation = {
  id: string;
  investorId: string;
  investorName: string;
  managerId: string;
  managerName: string;
  allocatedAmount: number;
  percentage: number;
  status: 'active' | 'pending' | 'completed';
  createdAt: string;
  lastUpdated: string;
};

const MAMAllocations = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const allocations: Allocation[] = [
    {
      id: 'MALLOC001',
      investorId: 'MINV001',
      investorName: 'Alice Williams',
      managerId: 'MGR001',
      managerName: 'John Smith',
      allocatedAmount: 50000,
      percentage: 20,
      status: 'active',
      createdAt: '2024-01-15',
      lastUpdated: '2024-01-20',
    },
    {
      id: 'MALLOC002',
      investorId: 'MINV002',
      investorName: 'Charlie Brown',
      managerId: 'MGR002',
      managerName: 'Jane Doe',
      allocatedAmount: 30000,
      percentage: 16.7,
      status: 'active',
      createdAt: '2024-01-10',
      lastUpdated: '2024-01-18',
    },
    {
      id: 'MALLOC003',
      investorId: 'MINV003',
      investorName: 'Diana Miller',
      managerId: 'MGR003',
      managerName: 'Bob Johnson',
      allocatedAmount: 20000,
      percentage: 21.1,
      status: 'pending',
      createdAt: '2024-01-08',
      lastUpdated: '2024-01-08',
    },
  ];

  const columns: DataTableColumn<Allocation>[] = [
    {
      id: 'id',
      header: 'Allocation ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '140px',
    },
    {
      id: 'investorName',
      header: 'Investor',
      accessor: row => row.investorName,
      sortable: true,
      sortValue: row => row.investorName,
      width: '180px',
    },
    {
      id: 'managerName',
      header: 'Manager',
      accessor: row => row.managerName,
      sortable: true,
      sortValue: row => row.managerName,
      width: '180px',
    },
    {
      id: 'allocatedAmount',
      header: 'Allocated Amount',
      accessor: row => `$${row.allocatedAmount.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.allocatedAmount,
      width: '160px',
    },
    {
      id: 'percentage',
      header: 'Allocation %',
      accessor: row => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500"
              style={{ width: `${row.percentage}%` }}
            />
          </div>
          <span className="font-semibold">{row.percentage.toFixed(1)}%</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.percentage,
      width: '160px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge
          className={
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
          }
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: row => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.createdAt,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('Edit:', row.id)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('Delete:', row.id)}
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      ),
      width: '120px',
    },
  ];

  const filteredAllocations = allocations.filter(allocation => {
    return (
      searchTerm === '' ||
      allocation.investorName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      allocation.managerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allocation.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalAllocated = allocations.reduce(
    (sum, alloc) => sum + alloc.allocatedAmount,
    0
  );

  const distributionData = [
    { label: 'John Smith', amount: 50000, percentage: 50, color: '#3b82f6' },
    { label: 'Jane Doe', amount: 30000, percentage: 30, color: '#10b981' },
    { label: 'Bob Johnson', amount: 20000, percentage: 20, color: '#f59e0b' },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Allocation Management"
          description="Manage investor fund allocations to MAM managers"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ProfitDistributionPanel
          totalProfit={totalAllocated}
          distributions={distributionData}
          period="Current Allocations"
        />

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Allocation Summary</CardTitle>
            <CardDescription>
              Quick overview of current allocations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Total Allocations
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {allocations.length}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Active Allocations
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {allocations.filter(a => a.status === 'active').length}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Total Amount Allocated
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  ${totalAllocated.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Pending Allocations
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  {allocations.filter(a => a.status === 'pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Allocations</CardTitle>
              <CardDescription>
                Manage and monitor all investor allocations
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                New Allocation
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredAllocations}
            searchPlaceholder="Search by investor, manager, or allocation ID..."
            onSearchChange={setSearchTerm}
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default MAMAllocations;
